const DEFAULT_BRAND = "#083e60";

/**
 * Relative luminance (WCAG) of a #rgb or #rrggbb colour, or null when the value is not a hex colour.
 */
export function relativeLuminance(color) {
    const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(color ?? "").trim());
    if (!match) return null;
    const hex = match[1].length === 3 ? [...match[1]].map((digit) => digit + digit).join("") : match[1];
    const [red, green, blue] = [0, 2, 4].map((offset) => {
        const channel = parseInt(hex.slice(offset, offset + 2), 16) / 255;
        return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

/**
 * Accent colour of the reservation form. Some themes use a near-white "main" colour for their
 * chrome (YServices) and carry their accent in "main-color-light"; a layout theme such as
 * Manhattan overrides both through CSS variables. White text must stay readable on the accent.
 */
export function resolveBookingBrand({ main = null, light = null, layoutStyle = {} } = {}) {
    const candidates = [layoutStyle?.["--main-color"], main, layoutStyle?.["--main-color-light"], light];
    let brand =
        candidates.find((color) => {
            const luminance = relativeLuminance(color);
            return luminance !== null && luminance < 0.5;
        }) ?? DEFAULT_BRAND;
    // Preserve the hue with AA contrast both for white button text and text on the 8% selected tint.
    const hexColor = (channels) => "#" + channels.map((channel) => channel.toString(16).padStart(2, "0")).join("");
    let channels = String(brand).replace(/^#/, "");
    if (channels.length === 3) channels = [...channels].map((digit) => digit + digit).join("");
    channels = [0, 2, 4].map((offset) => parseInt(channels.slice(offset, offset + 2), 16));
    const selectedLuminance = () => relativeLuminance(hexColor(channels.map((channel) => Math.round(channel * 0.08 + 255 * 0.92))));
    while ((selectedLuminance() + 0.05) / (relativeLuminance(brand) + 0.05) < 4.5) {
        channels = channels.map((channel) => Math.floor(channel * 0.95));
        brand = hexColor(channels);
    }
    return brand;
}

/**
 * Inline style shared by the reservation dialog and the other booking forms that reuse its components.
 */
export function bookingThemeStyle(themeStyle = {}, layoutStyle = {}) {
    return {
        ...(layoutStyle ?? {}),
        "--booking-brand": resolveBookingBrand({
            main: themeStyle?.["main-color"],
            light: themeStyle?.["main-color-light"],
            layoutStyle,
        }),
    };
}
